/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import { ChatNotifications } from 'bundles/ai-coach-platform/components/patterns/chat/notifications/ChatNotifications';
import type { MemoryNotificationConfig } from 'bundles/ai-coach-platform/components/patterns/chat/notifications/types';
import MemoryPreferenceNotification, {
  isNotificationDismissed,
} from 'bundles/ai-coach-platform/components/patterns/chat/settings/pages/memory/MemoryPreferenceNotification';

type Props = {
  enableContinuousChat: boolean;
  showSettings: boolean;
  settingsOpen: boolean;
  isCoachStudio: boolean;
  isMessageLoading: boolean;
  memoryNotificationConfig?: MemoryNotificationConfig;
  enableMemory?: boolean;
  brandLabel?: string;
};

const ChatHeaderNotifications = ({
  enableContinuousChat,
  showSettings,
  settingsOpen,
  isCoachStudio,
  isMessageLoading,
  memoryNotificationConfig,
  enableMemory = true,
  brandLabel,
}: Props): React.ReactElement | null => {
  const [showMemoryPreferenceNotification, setShowMemoryPreferenceNotification] = React.useState<boolean>(false);
  const [isAllNotificationsResolved, setIsAllNotificationsResolved] = React.useState(false);

  React.useEffect(() => {
    if (!isCoachStudio && !isMessageLoading) {
      setShowMemoryPreferenceNotification(!isNotificationDismissed());
    }
  }, [isCoachStudio, isMessageLoading]);

  const handleAllNotificationsResolved = React.useCallback<() => void>(() => {
    // slight delay to show the resolved state for a bit
    setTimeout(() => setIsAllNotificationsResolved(true), 500);
  }, []);

  if (settingsOpen) {
    return null;
  }

  if (!isAuthenticatedUser() && !isAllNotificationsResolved) {
    return <ChatNotifications onAllResolved={handleAllNotificationsResolved} />;
  }

  if (!enableContinuousChat && showSettings && showMemoryPreferenceNotification && enableMemory) {
    return (
      <MemoryPreferenceNotification
        onDismiss={() => setShowMemoryPreferenceNotification(false)}
        title={memoryNotificationConfig?.title}
        content={memoryNotificationConfig?.content}
        brandLabel={brandLabel}
      />
    );
  }

  if (enableContinuousChat && !isAllNotificationsResolved) {
    return (
      <ChatNotifications
        onAllResolved={handleAllNotificationsResolved}
        memoryNotificationConfig={memoryNotificationConfig}
        enableMemory={enableMemory}
        brandLabel={brandLabel}
      />
    );
  }

  return null;
};

export default ChatHeaderNotifications;
